/**
 * Per-user location scope — the pure rules, shared by ruby-hq and
 * document-manager.
 *
 * `users.clinics` scopes a staff user to CLIENTS; `users.location_scope`
 * (migration 0161) scopes one level down, to a client's `locations`. This file
 * holds every decision that can be made without I/O, so the two services (and
 * the four SQL predicates built from them) cannot drift.
 *
 * Zero dependencies — it sits beside `document-session-token.ts`, which both
 * services already import, and is safe in any bundle.
 *
 * The stored shape is `{ "<clientId>": ["<locationId>" | "__needs__", ...] }`:
 *
 *   - **no column, or no key for the client → `null` here = UNRESTRICTED.**
 *     This is what every existing user reads as, which is what makes the
 *     feature ship dark.
 *   - a list → only those sites, plus the "Unknown location" bucket when the
 *     sentinel is present.
 *   - an empty list → `{sites: [], unknown: false}`: nothing. The admin API
 *     refuses to write one, but the read path honours it (fail closed) so a
 *     value that arrives by any other route cannot widen access.
 *
 * ⚠️ **`null` (unrestricted) and `{sites: [], unknown: false}` (nothing) are
 * opposites.** Every consumer must branch on `null` explicitly; a truthiness
 * check reads the second as the first and grants everything. That is why the
 * helpers below take `AllowedLocations | null` rather than a bare array.
 *
 * Plan: docs/plans/2026-09-20-user-location-scope.md
 */
/**
 * The "Unknown location" bucket, as a scope entry.
 *
 * Byte-identical to `LOCATION_FILTER_UNKNOWN` (`@ruby/database`
 * `interactions.repo.ts`) and `LOCATION_NEEDS` (ruby-hq's
 * `client/ui/LocationFilter.tsx`) — the value the browser already puts on the
 * wire for "rows with no site". Reusing it means one sentinel across the
 * filter, the scope and both SQL predicates rather than three that must agree.
 *
 * It can never collide with a real location id: `LOCATION_ID_REGEX` is
 * `/^[a-z][a-z0-9-]{0,63}$/`, so an id cannot begin with an underscore.
 */
export const UNKNOWN_LOCATION_ID = '__needs__';
/**
 * Resolve a user's scope at one client.
 *
 * Returns `null` for UNRESTRICTED — no scope stored at all, or none for this
 * client. Callers must treat `null` as "apply no location constraint"; see the
 * warning in the module docstring.
 *
 * A malformed value (not an array) reads as unrestricted rather than throwing:
 * the column is hand-editable JSONB, and a broken row should degrade to
 * today's behaviour, which the admin can then see and fix, rather than locking
 * someone out of a clinic with no way to explain it. Entries that are not
 * strings are dropped.
 */
export function allowedLocationsFor(scope, clientId) {
    if (!scope || typeof scope !== 'object')
        return null;
    const entry = scope[clientId];
    if (entry === undefined || entry === null)
        return null;
    if (!Array.isArray(entry))
        return null;
    const sites = [];
    let unknown = false;
    for (const raw of entry) {
        if (typeof raw !== 'string')
            continue;
        const value = raw.trim();
        if (value.length === 0)
            continue;
        if (value === UNKNOWN_LOCATION_ID) {
            unknown = true;
        }
        else if (!sites.includes(value)) {
            sites.push(value);
        }
    }
    return { sites, unknown };
}
/** Is this real site readable by the user? Unrestricted (`null`) always passes. */
export function isSiteAllowed(allowed, locationId) {
    if (allowed === null)
        return true;
    if (!locationId)
        return false;
    return allowed.sites.includes(locationId);
}
/**
 * Is a row's recorded site readable, given the sites that currently EXIST for
 * the client?
 *
 * This is the TypeScript twin of the SQL scope predicates, and the
 * `existingSiteIds` argument is what keeps the two honest: both
 * `UNKNOWN_LOCATION_PREDICATE_SQL` and `UNLOCATED_PREDICATE_SQL` treat a
 * DANGLING id — one whose location has been deleted — as Unknown, not as that
 * site. Without the existing-site set a gate would read a dangling id as an
 * ordinary out-of-scope site and 403 a row that the list it came from happily
 * showed to an Unknown holder.
 *
 * `locationId` null/empty → the Unknown bucket.
 */
export function isRecordedSiteAllowed(allowed, locationId, existingSiteIds) {
    if (allowed === null)
        return true;
    if (!locationId)
        return allowed.unknown;
    // Dangling pointer — the SQL Unknown predicates claim it, so we must too.
    if (!existingSiteIds.includes(locationId))
        return allowed.unknown;
    return allowed.sites.includes(locationId);
}
/**
 * Clamp a LIST filter's requested location to what the user may see.
 *
 * Returns the value the query should use, or `undefined` for "no location
 * filter" (the scope itself still narrows the query — this only decides the
 * user's own extra filter).
 *
 * ⚠️ **Out-of-scope values are DROPPED, never rejected.** This mirrors
 * `parseLocationFilter`'s existing posture for stale bookmarks, and it is
 * required rather than merely tidy: a user scoped to ONE site has the location
 * control hidden (the UI treats them as a single-site clinic), so a 403 on a
 * bookmarked `?location=` would be an error they have no control to clear.
 */
export function clampLocationFilter(allowed, requested) {
    if (!requested)
        return undefined;
    if (allowed === null)
        return requested;
    if (requested === UNKNOWN_LOCATION_ID)
        return allowed.unknown ? requested : undefined;
    return allowed.sites.includes(requested) ? requested : undefined;
}
/**
 * Does this scope permit nothing at all? `{sites: [], unknown: false}`.
 *
 * The admin API refuses to store it, so in practice this catches a row written
 * by hand or by a script. Callers use it to answer with an empty list rather
 * than running a query whose `ANY('{}')` can never match.
 */
export function allowsNothing(allowed) {
    return allowed !== null && allowed.sites.length === 0 && !allowed.unknown;
}
/**
 * Does this scope permit any REAL site? A user granted only the Unknown bucket
 * has no site a calendar, a room list or a booking could use.
 */
export function hasAnySite(allowed) {
    return allowed === null || allowed.sites.length > 0;
}
/**
 * Inverse of `allowedLocationsFor`: turn a resolved scope back into the STORED
 * shape for one client, so it can ride a signed token.
 *
 * Needed because a service-to-service hop (the dashboard's tallies fetch) holds
 * a resolved scope but must sign the wire format. `null` in → `undefined` out,
 * i.e. an unrestricted user signs no claim at all.
 */
export function toLocationScope(allowed, clientId) {
    // Both spellings of "unrestricted" are accepted: the pure helpers return
    // `null`, while the query-option types use an optional field.
    if (allowed === null || allowed === undefined)
        return undefined;
    return {
        [clientId]: [...allowed.sites, ...(allowed.unknown ? [UNKNOWN_LOCATION_ID] : [])],
    };
}
/** Narrow a list of location-bearing items to the scope's real sites. */
export function filterToAllowedSites(allowed, items, getId) {
    if (allowed === null)
        return [...items];
    return items.filter((item) => allowed.sites.includes(getId(item)));
}
